import { useState, useCallback } from 'react';
import { downloadReport, openReportPdf } from '../api/reports';
import type { ReportType, ReportFilters, ReportFormat } from '../types/report';

interface UseDownloadReportReturn {
  download: (
    reportType: ReportType,
    format?: ReportFormat,
    filters?: ReportFilters
  ) => Promise<void>;
  openPdf: (reportType: ReportType, filters?: ReportFilters) => Promise<void>;
  isDownloading: boolean;
  downloadingReport: ReportType | null;
  error: string | null;
}

/**
 * Hook for downloading reports with loading and error state management
 */
export function useDownloadReport(): UseDownloadReportReturn {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState<ReportType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(
    async (
      reportType: ReportType,
      format: ReportFormat = 'xlsx',
      filters?: ReportFilters
    ) => {
      setIsDownloading(true);
      setDownloadingReport(reportType);
      setError(null);

      try {
        await downloadReport(reportType, format, filters);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to download report';
        setError(message);
        throw err;
      } finally {
        setIsDownloading(false);
        setDownloadingReport(null);
      }
    },
    []
  );

  const openPdf = useCallback(async (reportType: ReportType, filters?: ReportFilters) => {
    setIsDownloading(true);
    setDownloadingReport(reportType);
    setError(null);

    try {
      await openReportPdf(reportType, filters);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open report';
      setError(message);
      throw err;
    } finally {
      setIsDownloading(false);
      setDownloadingReport(null);
    }
  }, []);

  return {
    download,
    openPdf,
    isDownloading,
    downloadingReport,
    error,
  };
}
